import React, { useState, useEffect } from 'react';
import { Check, Plus, Loader2, Globe, Sparkles, Mail } from 'lucide-react';
import { Button } from '../common/Button';
import subscriptionService, { SubscriptionPlan, CouponValidationResult } from '../../services/subscriptionService';
import { useToast } from '../common/Toast';
import PaymentModal from './PaymentModal';
import { GST_RATE } from '../../constants/gst';
import { locationService } from '../../services/locationService';
import { planTrialService } from '../../services/planTrialService';
import TrialRequestModal from './TrialRequestModal';

interface AddOnBundlesProps {
  activePlan: 'free' | 'monthly' | 'yearly' | 'quarterly';
  onSubscriptionUpdate?: () => void;
  couponDetails?: CouponValidationResult | null;
  onPlanSelected?: (planId: string) => void;
  shouldShowPricing?: boolean;
  userLocation?: { country: string; countryCode: string } | null;
}

export const AddOnBundles: React.FC<AddOnBundlesProps> = ({ 
  activePlan,
  onSubscriptionUpdate,
  couponDetails = null,
  onPlanSelected,
  shouldShowPricing = true,
  userLocation
}) => {
  const [addOns, setAddOns] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purchasingAddonId, setPurchasingAddonId] = useState<string | null>(null);
  const [selectedAddon, setSelectedAddon] = useState<SubscriptionPlan | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const { showToast } = useToast();
  
  const isPaidUser = activePlan !== 'free';
  
  // Trial modal state
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [trialAddonName, setTrialAddonName] = useState<string>('');
  
  // Handle add-on trial request for non-Indian users
  const handleAddonTrialRequest = async (addon: SubscriptionPlan) => {
    try {
      setPurchasingAddonId(addon._id);
      const response = await planTrialService.requestAddOnTrial(addon._id, addon.name);

      if (response.success) {
        showToast(response.message, 'success');
      } else {
        showToast(response.message, 'error');
      }
    } catch (error) {
      console.error('Error requesting add-on trial:', error);
      showToast('Failed to submit trial request. Please try again.', 'error');
    } finally {
      setPurchasingAddonId(null);
    }
  };
  
  // Handle add-on purchase
  const handlePurchaseAddon = async (addon: SubscriptionPlan) => {
    if (!isPaidUser) {
      showToast('You need an active subscription to purchase add-ons.', 'error');
      return;
    }
    
    // Notify parent about plan selection for coupon validation
    if (onPlanSelected) {
      onPlanSelected(addon._id);
    }
    
    try {
      setPurchasingAddonId(addon._id);
      // For free add-ons, directly subscribe
      if (addon.price === 0) {
        // Purchase the add-on bundle
        const result = await subscriptionService.subscribeToPlan(addon);
        
        // Check if result is a valid subscription object
        if (result && result._id && result.plan && result.isActive !== undefined) {
          // Success
          showToast(
            `You have successfully purchased the ${addon.name} add-on bundle.`,
            'success'
          );
          
          // Force refresh subscription data
          await subscriptionService.getUserSubscription(true);
          
          // Notify parent about subscription update
          if (onSubscriptionUpdate) {
            onSubscriptionUpdate();
          }
          
          // Reload the page to refresh subscription state
          window.location.reload();
        } else {
          // Invalid response
          console.error('Invalid add-on purchase response:', result);
          throw new Error('Invalid data received from server');
        }
      } else {
        // For paid add-ons, show payment modal
        setSelectedAddon(addon);
        setShowPaymentModal(true);
      }
    } catch (error) {
      console.error('AddOnBundles: Error purchasing add-on:', error);
      showToast(
        'There was an error processing your add-on purchase. Please try again later.',
        'error'
      );
    } finally {
      setPurchasingAddonId(null);
    }
  };
  
  // Get the adjusted add-on price if coupon is applied
  const getAdjustedAddonPrice = (addon: SubscriptionPlan) => {
    if (couponDetails && couponDetails.isValid) {
      // Check if coupon applies to a specific plan
      if (couponDetails.applicablePlans && couponDetails.applicablePlans.length > 0) {
        // Only apply discount if this add-on is in the applicable plans list
        if (!couponDetails.applicablePlans.includes(addon._id)) {
          return addon.currentPrice; // No discount for non-applicable add-ons
        }
      }
      
      // First check if we have pre-calculated discounts from the planDiscounts array
      if (couponDetails.planDiscounts && Array.isArray(couponDetails.planDiscounts)) {
        // Find the discount info for this specific addon
        const addonDiscount = couponDetails.planDiscounts.find(
          (discount: any) => discount.planId === addon._id
        );
        
        if (addonDiscount && typeof addonDiscount.discountedPrice === 'number') {
          console.log(`Using pre-calculated discount for addon ${addon.name} (${addon._id}): ${addonDiscount.discountedPrice}`);
          return addonDiscount.discountedPrice;
        }
      }
      
      // Legacy support for single plan discount
      if (couponDetails.planId === addon._id && typeof couponDetails.discountedPrice === 'number' && couponDetails.discountedPrice > 0) {
        console.log(`Using legacy discount for addon ${addon.name} (${addon._id}): ${couponDetails.discountedPrice}`);
        return couponDetails.discountedPrice;
      }
      
      // Calculate discount dynamically if needed
      if (isCouponApplicableForAddon(addon) && couponDetails.couponType !== 'free_minutes') {
        console.log(`Calculating discount on-the-fly for addon ${addon.name} (${addon._id})`);
        
        // Calculate discount based on coupon type
        if (couponDetails.couponType === 'percentage' && typeof couponDetails.value === 'number') {
          const discount = (addon.currentPrice * couponDetails.value) / 100;
          // Check if there's a maximum discount cap
          if (couponDetails.maxDiscountAmount && discount > couponDetails.maxDiscountAmount) {
            const cappedPrice = addon.currentPrice - couponDetails.maxDiscountAmount;
            console.log(`Capped discount: ${discount} > ${couponDetails.maxDiscountAmount}, price: ${cappedPrice}`);
            return cappedPrice;
          }
          const discountedPrice = addon.currentPrice - discount;
          console.log(`Percentage discount (${couponDetails.value}%): ${discount}, price: ${discountedPrice}`);
          return discountedPrice;
        } else if (couponDetails.couponType === 'fixed_amount' && typeof couponDetails.value === 'number') {
          const discountedPrice = Math.max(0, addon.currentPrice - couponDetails.value);
          console.log(`Fixed amount discount: ${couponDetails.value}, price: ${discountedPrice}`);
          return discountedPrice;
        }
      }
    }
    return addon.currentPrice;
  };
  
  // Check if coupon is applicable for this add-on
  const isCouponApplicableForAddon = (addon: SubscriptionPlan): boolean => {
    if (!couponDetails || !couponDetails.isValid) {
      return false;
    }
    
    // Check for plan restrictions
    if (couponDetails.applicablePlans && couponDetails.applicablePlans.length > 0) {
      return couponDetails.applicablePlans.includes(addon._id);
    }
    
    // If no plan restrictions, coupon applies to all plans
    return true;
  };
  
  // Check if add-on meets minimum purchase amount for coupon
  const meetsCouponMinimumPurchase = (addon: SubscriptionPlan): boolean => {
    if (!couponDetails || !couponDetails.isValid || !couponDetails.minPurchaseAmount) {
      return true; // No minimum requirement
    }
    
    return addon.currentPrice >= couponDetails.minPurchaseAmount;
  };
  
  // Get free minutes from coupon
  const getFreeMinutesFromCoupon = (addon: SubscriptionPlan) => {
    if (couponDetails && couponDetails.isValid && isCouponApplicableForAddon(addon)) {
      return couponDetails.freeMinutes || 0;
    }
    return 0;
  };
  
  // Check if this addon has a coupon applied
  const hasCouponApplied = (addon: SubscriptionPlan) => {
    return couponDetails?.isValid && isCouponApplicableForAddon(addon);
  };
  
  // Handle successful payment completion
  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    
    // Force refresh subscription data
    await subscriptionService.getUserSubscription(true);
    
    // Notify parent about subscription update
    if (onSubscriptionUpdate) {
      onSubscriptionUpdate();
    }
    
    // Reload the page to refresh UI with updated subscription state
    window.location.reload();
  };
  
  // Handle payment error
  const handlePaymentError = (message: string) => {
    showToast(message || 'Payment processing failed', 'error');
    setShowPaymentModal(false);
  };
  
  useEffect(() => {
    const fetchAddOns = async () => {
      try {
        setLoading(true);
        // Get subscription plans that are add-ons
        console.log('AddOnBundles: Fetching add-on bundles...');
        const fetchedAddOns = await subscriptionService.getSubscriptionPlansByType(true);
        console.log('AddOnBundles: Fetched add-on bundles:', fetchedAddOns);
        
        if (fetchedAddOns.length === 0) {
          console.warn('AddOnBundles: No add-on bundles returned from service');
        }
        
        setAddOns(fetchedAddOns);
        
        // If there are add-ons and onPlanSelected is provided, select the first add-on by default
        if (fetchedAddOns.length > 0 && onPlanSelected) {
          onPlanSelected(fetchedAddOns[0]._id);
        }
        
        setError(null);
      } catch (err) {
        console.error('AddOnBundles: Error fetching add-on bundles:', err);
        setError('Failed to load add-on bundles. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAddOns();
  }, [onPlanSelected]);

  // If loading, show a loading state
  if (loading) {
    return (
      <div className="text-base flex flex-col items-center justify-center py-12">
        <Loader2 className="w-12 h-12 text-primary-light dark:text-primary-dark animate-spin mb-4" />
        <p className="text-text-secondary-light dark:text-text-secondary-dark">
          Loading add-on bundles...
        </p>
      </div>
    );
  }

  // If there was an error, show an error message
  if (error) {
    return (
      <div className="p-6 rounded-xl bg-red-500/10 text-center">
        <p className="text-red-500 dark:text-red-400">{error}</p>
        <Button 
          variant="secondary"
          className="mt-4"
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </div>
    );
  }

  // If no add-ons are available, don't show the section
  if (addOns.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
          Add-On Bundles
        </h2>
      </div>

      {!isPaidUser && shouldShowPricing && (
        <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6 max-w-3xl text-sm">
          Purchase a monthly or yearly subscription to unlock add-on bundles for additional interview practice.
        </p>
      )}
      
      {isPaidUser && (
        <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6 max-w-3xl text-sm">
          Enhance your interview preparation with additional practice bundles that can be used alongside your subscription.
        </p>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {addOns.filter(addon => addon.isPurchasable === undefined || addon.isPurchasable).map((addon) => {
          // Apply coupon discounts if available
          const displayPrice = getAdjustedAddonPrice(addon);
          const originalPrice = hasCouponApplied(addon) && displayPrice < addon.currentPrice ? addon.currentPrice : undefined;
          const couponApplied = hasCouponApplied(addon);
          const freeMinutes = getFreeMinutesFromCoupon(addon);
          const totalMinutes = addon.interviewMinutes + freeMinutes;
          
          // Check if minimum purchase amount is met
          const meetsMinPurchase = meetsCouponMinimumPurchase(addon);
          
          // Calculate discount percentage for UI
          const discountPercentage = couponApplied && displayPrice < addon.currentPrice ? 
            Math.round(((addon.currentPrice - displayPrice) / addon.currentPrice) * 100) : 0;
          
          // Click handler to notify parent about plan selection
          const handleAddonClick = () => {
            if (onPlanSelected) {
              onPlanSelected(addon._id);
            }
          };
          
          return (
            <div key={addon._id} onClick={handleAddonClick}>
              <AddOnCard 
                name={addon.name}
                price={displayPrice}
                originalPrice={originalPrice}
                minutes={totalMinutes}
                freeMinutes={freeMinutes}
                features={formatAddOnFeatures(addon, freeMinutes)}
                disabled={!shouldShowPricing ? false : !isPaidUser}
                isPurchasing={purchasingAddonId === addon._id}
                hasCouponApplied={couponApplied}
                discountPercentage={discountPercentage}
                couponValidButNotApplicable={couponDetails?.isValid && !isCouponApplicableForAddon(addon)}
                couponMinPurchaseNotMet={!meetsMinPurchase && couponApplied}
                gstRate={GST_RATE}
                shouldShowPricing={shouldShowPricing}
                userLocation={userLocation}
                onPurchase={() => shouldShowPricing ? handlePurchaseAddon(addon) : handleAddonTrialRequest(addon)}
              />
            </div>
          );
        })}
      </div>
      
      {/* Payment Modal */}
      {selectedAddon && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          planId={selectedAddon._id}
          planName={selectedAddon.name}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          isRecurring={selectedAddon.isRecurring}
          couponCode={couponDetails?.isValid && isCouponApplicableForAddon(selectedAddon) ? couponDetails.code : undefined}
          couponDiscount={
            couponDetails?.isValid && couponDetails.planDiscounts 
              ? couponDetails.planDiscounts.find(pd => pd.planId === selectedAddon._id)?.discountAmount 
              : (couponDetails?.isValid && couponDetails.planId === selectedAddon._id ? couponDetails.discountAmount : undefined)
          }
          couponFreeMinutes={
            couponDetails?.isValid && couponDetails.planDiscounts 
              ? couponDetails.planDiscounts.find(pd => pd.planId === selectedAddon._id)?.freeMinutes 
              : (couponDetails?.isValid && couponDetails.planId === selectedAddon._id ? couponDetails.freeMinutes : undefined)
          }
        />
      )}
      
      {/* Trial Request Modal */}
      <TrialRequestModal
        visible={showTrialModal}
        onClose={() => setShowTrialModal(false)}
        planName={trialAddonName}
        userLocation={userLocation}
      />
    </div>
  );
};

// Helper function to format add-on features
const formatAddOnFeatures = (addon: SubscriptionPlan, freeMinutes: number = 0): string[] => {
  const features = [...addon.features];
  
  // Add interview minutes feature if not already included
  if (!features.some(feature => feature.includes('minutes'))) {
    if (freeMinutes > 0) {
      features.unshift(`${addon.interviewMinutes + freeMinutes} minutes (includes ${freeMinutes} free minutes)`);
    } else {
      features.unshift(`${addon.interviewMinutes} minutes of additional practice`);
    }
  }
  
  // Add validity feature if not already included
  if (!features.some(feature => feature.toLowerCase().includes('valid'))) {
    features.push('Valid till Plan Expiry');
  }
  
  return features;
};

interface AddOnCardProps {
  name: string;
  price: number;
  originalPrice?: number;
  minutes: number;
  freeMinutes?: number;
  features: string[];
  disabled: boolean;
  isPurchasing?: boolean;
  hasCouponApplied?: boolean;
  discountPercentage?: number;
  couponValidButNotApplicable?: boolean;
  couponMinPurchaseNotMet?: boolean;
  gstRate: number;
  shouldShowPricing: boolean;
  userLocation?: { country: string; countryCode: string } | null;
  onPurchase: () => void;
}

const AddOnCard: React.FC<AddOnCardProps> = ({ 
  name, 
  price, 
  originalPrice,
  minutes, 
  freeMinutes = 0,
  features,
  disabled,
  isPurchasing = false,
  hasCouponApplied = false,
  discountPercentage = 0,
  couponValidButNotApplicable = false,
  couponMinPurchaseNotMet = false,
  gstRate,
  shouldShowPricing,
  userLocation,
  onPurchase
}) => {
  return (
    <div className={`
      relative rounded-2xl transition-transform duration-300 ease-out hover:scale-[1.02]
      ${disabled ? 'opacity-70' : ''}
    `}>
      {/* Overlay for disabled state */}
      {disabled && (
        <div className="absolute inset-0 bg-background-light/10 dark:bg-background-dark/10 rounded-2xl z-10 backdrop-blur-[0.7px]"></div>
      )}
      
      {/* Coupon badge */}
      {hasCouponApplied && discountPercentage > 0 && (
        <div className="absolute -top-0 -right-0 z-10">
            <div className="bg-green-500 text-white px-4 py-1 rounded-bl-lg rounded-tr-xl text-sm font-semibold font-heading">
            {discountPercentage}% Off Applied
          </div>
        </div>
      )}
      {freeMinutes !== undefined && freeMinutes > 0 && (
        <div className="absolute -top-0 -right-0 z-10">
          <div className="bg-green-500 text-white px-4 py-1 rounded-bl-lg rounded-tr-xl text-sm font-semibold font-heading">
            Free additional {freeMinutes} minutes
          </div>
        </div>
      )}
      {/* Coupon not applicable badge, commented out for now */}
      {/* {couponValidButNotApplicable && (
        <div className="absolute -top-0 -right-0 z-10">
          <div className="bg-amber-500 text-white px-3 py-1 rounded-bl-lg rounded-tr-xl text-xs font-medium shadow-md">
            Coupon not applicable
          </div>
        </div>
      )} */}
      
      {/* Card with gradient border */}
      <div className="p-0 rounded-2xl bg-gradient-to-br from-primary-light/5 via-secondary-light/5 to-primary-light/5 dark:from-primary-dark/5 dark:via-secondary-dark/5 dark:to-primary-dark/5">
        {/* Glass container */}
        <div className="relative h-full p-6 rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold mb-2 text-text-primary-light dark:text-text-primary-dark">
                {name}
              </h3>
              <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm">
                {minutes} {freeMinutes > 0 && <span className="text-green-600 dark:text-green-400">(includes {freeMinutes} free)</span>} Minutes
              </p>
            </div>
            <div className={`text-right ${(freeMinutes !== undefined && freeMinutes > 0) || hasCouponApplied && discountPercentage > 0 ? 'mt-2' : ''}`}>
              {shouldShowPricing ? (
                <>
                  {originalPrice && (
                    <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark line-through mb-1">
                      ₹{originalPrice.toFixed(0)}
                    </div>
                  )}
                  <div className={`text-2xl font-bold ${hasCouponApplied ? 'text-green-600 dark:text-green-500' : 'text-primary-light dark:text-primary-dark'}`}>
                    {price === 0 ? '₹1' : `₹${price.toFixed(0)}`}
                  </div>
                  {/* GST Information - Only show if GST rate is greater than 0 */}
                  {price > 0 && gstRate > 0 && (
                    <div className="text-right">
                      <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark font-heading font-medium">
                        +{gstRate}% GST
                      </p>
                    </div>
                  )}
                  <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm">
                    one-time
                  </p>
                </>
              ) : (
                <div className="text-center">
                  <div className="text-lg font-bold text-primary-light dark:text-primary-dark mb-1">
                    Request Trial
                  </div>
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                    {userLocation?.country} pricing
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Features */}
          <ul className="space-y-3 mb-6">
            {features.filter(feature => feature.trim() !== '').map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className={`mt-1 p-0.5 rounded-full ${
                  feature.includes('free minutes') 
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                    : 'bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark'
                }`}>
                  <Check className="w-3 h-3" />
                </span>
                <span className={`text-sm ${
                  feature.includes('free minutes')
                    ? 'text-green-700 dark:text-green-300 font-medium'
                    : 'text-text-secondary-light dark:text-text-secondary-dark'
                }`}>
                  {feature}
                </span>
              </li>
            ))}
          </ul>

          {/* CTA Button */}
          <Button 
            variant="secondary"
            onClick={onPurchase}
            disabled={disabled || isPurchasing}
            className={disabled ? 'opacity-60 cursor-not-allowed' : ''}
            leftIcon={!isPurchasing && shouldShowPricing ? <Plus className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
            loading={isPurchasing}
          >
            {isPurchasing ? 'Processing...' : shouldShowPricing ? 'Buy Bundle' : 'Request Trial'}
          </Button>
        </div>
      </div>
    </div>
  );
}; 